import {
  Avatar,
  Box,
  Button,
  Dropdown,
  DropdownItem,
  InputField,
  Modal,
  Typography,
  useToast,
} from "fds";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { nanoid } from "nanoid";
import utilsCommon from "../../shared/utils/common";
import useAdminStorageStore from "../../redux/dispatcher/useAdminStorageStore";
import { StorageInfoDto } from "../../types/adminStorage";
import utilsDocs from "../../shared/utils/docs";

export type ModalProps = {
  serverKey: string;
  showDetailModal: boolean;
  toggleDetailModal: (open: boolean) => void;
  checkStatus: (status: string) => React.ReactNode;
};

const StorageDetailModal: React.FC<ModalProps> = ({
  serverKey,
  showDetailModal,
  toggleDetailModal,
  checkStatus,
}) => {
  const {
    adminStorageStore,
    boundGetStorageDetail,
    boundCheckStorageStatus,
    boundUpdateStorageStatus,
    boundGetStorageStoppedList,
    boundDeleteStorage,
  } = useAdminStorageStore();
  const toast = useToast();
  const { t } = useTranslation();

  const [storageInfo, setStorageInfo] = useState({} as StorageInfoDto);
  const [stoppedStorageList, setStoppedStorageList] = useState(
    [] as StorageInfoDto[]
  );
  const [storageName, setStorageName] = useState("");
  const [transferStorageKey, setTransferStorageKey] = useState("all");
  const [isEditMode, setEditMode] = useState(false);
  const [dropdownClick, setDropdownClick] = useState(0);

  useEffect(() => {
    boundGetStorageDetail(serverKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const detail = adminStorageStore.storageDetail;
    setStorageInfo(detail);
    setStorageName(detail.name ?? "");
    if (detail?.status === "P") {
      boundGetStorageStoppedList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminStorageStore.storageDetail]);

  useEffect(() => {
    setStoppedStorageList(adminStorageStore.storageStoppedList);
  }, [adminStorageStore.storageStoppedList]);

  const calcServerUsage = (usageSize: number, totalSize: number) => {
    if (totalSize === 0) return t("common.unlimited");
    const usagePercent = (usageSize * 100) / totalSize;
    return (
      usagePercent.toFixed(2) +
      "% " +
      t("common.use") +
      "(" +
      utilsDocs.fileSizeFormat(usageSize) +
      " / " +
      utilsDocs.fileSizeFormat(totalSize) +
      ")"
    );
  };

  const moveFiletoOtherServer = () => {
    const targetStorage = stoppedStorageList.find(
      (item) => item.serverKey === transferStorageKey
    );
    if (!targetStorage) return;
    const protocolHeader = targetStorage.sslp === "N" ? "http://" : "https://";
    boundUpdateStorageStatus({
      serverKey: storageInfo.serverKey,
      status: "M",
      targetUrl: protocolHeader + targetStorage.url + ":" + targetStorage.port,
      ver: storageInfo.ver ?? "",
    });
    toggleDetailModal(false);
  };

  const checkStorageConnect = async () => {
    const result: any = await boundCheckStorageStatus(storageInfo.serverKey);
    if (result?.payload?.connect === true) {
      toast.toastMsg(
        nanoid(),
        t("common.alert.storageServerConnected"),
        "success"
      );
    } else {
      toast.toastMsg(
        nanoid(),
        t("common.alert.storageServerConnected"),
        "error"
      );
    }
  };

  const updateStatus = (status: string) => {
    switch (status) {
      case "R":
        return (
          <Button
            btnStyle="fill"
            type="secondary"
            size="sm"
            classes="mg ml-8"
            onClick={() =>
              boundUpdateStorageStatus({
                serverKey: storageInfo.serverKey,
                status: "P",
                ver: storageInfo.ver ?? "",
              })
            }
            label={t("storage.status.inactive")}
          ></Button>
        );
      case "P":
        return (
          <Button
            btnStyle="fill"
            type="secondary"
            size="sm"
            classes="mg ml-8"
            onClick={() =>
              boundUpdateStorageStatus({
                serverKey: storageInfo.serverKey,
                status: "R",
                ver: storageInfo.ver ?? "",
              })
            }
            label={t("storage.status.activeAgain")}
          ></Button>
        );
      case "E":
        return (
          <Box>
            <Button
              btnStyle="fill"
              type="secondary"
              size="sm"
              classes="mg ml-8"
              onClick={() =>
                boundUpdateStorageStatus({
                  serverKey: storageInfo.serverKey,
                  status: "S",
                  ver: storageInfo.ver ?? "",
                })
              }
              label={t("storage.status.inactive")}
            />

            <Button
              btnStyle="fill"
              type="secondary"
              size="sm"
              classes="mg ml-8"
              onClick={() => checkStorageConnect()}
              label={t("storage.test")}
            />
          </Box>
        );
      case "S":
        return (
          <Button
            btnStyle="fill"
            type="secondary"
            size="sm"
            classes="mg ml-8"
            onClick={() =>
              boundUpdateStorageStatus({
                serverKey: storageInfo.serverKey,
                status: "E",
                ver: storageInfo.ver ?? "",
              })
            }
            label={t("storage.again")}
          />
        );
      case "M":
        return (
          <Button
            btnStyle="fill"
            type="secondary"
            size="sm"
            classes="mg ml-8"
            onClick={() =>
              boundUpdateStorageStatus({
                serverKey: storageInfo.serverKey,
                status: "P",
                ver: storageInfo.ver ?? "",
              })
            }
            label={t("storage.status.deleteTransfer")}
          />
        );
      case "C":
        return (
          <Button
            btnStyle="fill"
            type="secondary"
            size="sm"
            classes="mg ml-8"
            onClick={() => {
              boundDeleteStorage(storageInfo.serverKey);
              toggleDetailModal(false);
            }}
            label={t("common.delete")}
          />
        );
      default:
        return (
          <Box>
            <Button
              btnStyle="fill"
              type="secondary"
              size="sm"
              classes="mg ml-8"
              onClick={() =>
                boundUpdateStorageStatus({
                  serverKey: storageInfo.serverKey,
                  status: "P",
                  ver: storageInfo.ver ?? "",
                })
              }
              label={t("storage.status.inactive")}
            />
            <Button
              btnStyle="fill"
              type="secondary"
              size="sm"
              classes="mg ml-8"
              onClick={() => checkStorageConnect()}
              label={t("storage.status.checkConnection")}
            />
          </Box>
        );
    }
  };

  return (
    <Modal
      open={showDetailModal}
      onClose={() => toggleDetailModal(false)}
      title={t("storage.serverDetail")}
      footer={
        <Box justifyContent="flex-end">
          <Button
            btnStyle="fill"
            onClick={() => toggleDetailModal(false)}
            label={t("common.ok")}
          />
        </Box>
      }
      body={
        <Box direction="column" width="100%">
          <Box alignItems="center" direction="row">
            <Avatar
              name={storageInfo.name}
              size="48"
              shape="team"
              uniqueCode={serverKey}
            />
            {isEditMode ? (
              <InputField
                size="md"
                value={storageName}
                classes="storageDetail-nameText mg ml-12"
                onChange={(e) => setStorageName(e.target.value)}
                maxLength={255}
                placeholder={""}
              />
            ) : (
              <Typography fontSize={16} classes="mg ml-12" width={440} ellipsis>
                {storageName}
              </Typography>
            )}
            <Box classes="storageDetail-titleBtn">
              {isEditMode ? (
                <Box>
                  <Button
                    btnStyle="fill"
                    type="secondary"
                    onClick={() => {
                      setStorageName(storageInfo.name);
                      setEditMode(false);
                    }}
                    classes="mg ml-8"
                    label={t("common.cancel")}
                  />
                  <Button
                    btnStyle="fill"
                    classes="mg ml-8"
                    onClick={() => {
                      boundUpdateStorageStatus({
                        name: storageName,
                        serverKey: storageInfo.serverKey,
                        status: storageInfo.status,
                        ver: storageInfo.ver ?? "",
                      });
                      setEditMode(false);
                    }}
                    state={
                      storageInfo.name === storageName ||
                      storageName.trim() === ""
                        ? "disable"
                        : "default"
                    }
                    label={t("common.ok")}
                  />
                </Box>
              ) : (
                <Button
                  btnStyle="fill"
                  onClick={() => setEditMode(true)}
                  label={t("common.change")}
                />
              )}
            </Box>
          </Box>

          <Box classes="storageDetail-body" direction="column">
            <Box classes="mg mb-10">
              <Box width="30%">{t("storage.representServer")}</Box>
              {storageInfo.defaultFlag === "Y" && (
                <Box alignItems="center">
                  <svg
                    width="16px"
                    height="16px"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.74581 7.17085C2.51801 6.94305 2.14866 6.94305 1.92085 7.17085C1.69305 7.39866 1.69305 7.76801 1.92085 7.99581L4.83752 10.9125C5.06533 11.1403 5.43467 11.1403 5.66248 10.9125L12.0791 4.49581C12.307 4.26801 12.307 3.89866 12.0791 3.67085C11.8513 3.44305 11.482 3.44305 11.2542 3.67085L5.25 9.67504L2.74581 7.17085Z"
                      fill={
                        window.eco.companyName === "workspace"
                          ? "#6623e8"
                          : "#25c16f"
                      }
                    />
                  </svg>
                  <Typography classes="mg ml-4">
                    {t("storage.defaultStorage")}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box classes="mg mb-10">
              <Box width="30%">{t("storage.serverDate")}</Box>
              <Box>{utilsCommon.getDateStr(storageInfo.createTime)}</Box>
            </Box>
            <Box classes="mg mb-10">
              <Box width="30%">{t("storage.storageUsage")}</Box>
              <Box>
                {storageInfo.usageSize === -1 ||
                (storageInfo.status !== "P" && storageInfo.status !== "R")
                  ? "-"
                  : calcServerUsage(
                      storageInfo.usageSize ?? 0,
                      storageInfo.totalSize ?? 0
                    )}
              </Box>
            </Box>
            <Box direction="column">
              <Typography classes="mg mb-10">
                {t("storage.accessInfo")}
              </Typography>
              <Box classes="storageDetail-accessInfo" direction="column">
                <Box classes="storageDetail-row">
                  <Box classes="storageDetail-label">
                    {t("storage.ipDomain")}
                  </Box>
                  <Box>{storageInfo.url}</Box>
                </Box>
                <Box classes="storageDetail-row">
                  <Box classes="storageDetail-label">{t("storage.port")}</Box>
                  <Box>{storageInfo.port}</Box>
                </Box>
                <Box classes="storageDetail-row">
                  <Box classes="storageDetail-label">
                    {t("storage.serverStatus")}
                  </Box>
                  <Box>
                    {checkStatus(storageInfo.status)}
                    {storageInfo.defaultFlag !== "Y" &&
                      updateStatus(storageInfo.status)}
                    {storageInfo.defaultFlag === "Y" && (
                      <Button
                        btnStyle="fill"
                        type="secondary"
                        size="sm"
                        classes="mg ml-8"
                        onClick={() => checkStorageConnect()}
                        label={t("storage.status.checkConnection")}
                      />
                    )}
                    {storageInfo.status === "P" && (
                      <Box classes="mg mt-10" alignItems="center">
                        <Dropdown
                          btnSize="sm"
                          value={
                            stoppedStorageList.find(
                              (i) => i.serverKey === transferStorageKey
                            )?.name ?? t("storage.chooseServer")
                          }
                          buttonClick={dropdownClick}
                          width={300}
                        >
                          <DropdownItem
                            label={t("storage.chooseServer")}
                            onClick={() => {
                              setTransferStorageKey("all");
                              setDropdownClick((c) => c + 1);
                            }}
                          />
                          {stoppedStorageList.map((item) => (
                            <DropdownItem
                              key={item.serverKey}
                              label={item.name}
                              onClick={() => {
                                setTransferStorageKey(item.serverKey);
                                setDropdownClick((c) => c + 1);
                              }}
                            />
                          ))}
                        </Dropdown>
                        <Button
                          btnStyle="fill"
                          classes="mg ml-10"
                          onClick={() => moveFiletoOtherServer()}
                          label={t("storage.move")}
                          state={
                            transferStorageKey === "all" ? "disable" : "default"
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      }
    />
  );
};

export default StorageDetailModal;
